import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as lastChanceApi from '@apis/last-chance';
import LastChanceStatusTag from '@components/LastChanceStatusTag';
import LastChanceSalesStatusModal from '@components/last-chance/LastChanceSalesStatusModal';
import { LastChanceAdminQuery, LastChanceDisplayStatus, LastChanceProductListItem } from '@models/last-chance';
import { isLastChanceSaleEnded, LAST_CHANCE_DISPLAY_STATUS_LABELS } from '@utils/last-chance-status';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tooltip,
  message,
} from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
import { PRODUCT_SHARE_BASE_URL } from '@variables';

const COPY_TOAST_OPTIONS = {
  position: 'top-center' as const,
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: false,
  draggable: true,
  progress: undefined,
  bodyStyle: {
    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
    textAlign: 'center' as const,
    fontFamily: 'Pretendard',
    fontSize: '14px',
    fontStyle: 'normal' as const,
    fontWeight: 500,
    lineHeight: '20px',
  },
  theme: 'dark' as const,
};

const DISPLAY_STATUS_TABS: { label: string; value: string }[] = [
  { label: '전체', value: '' },
  ...(['active', 'fulfilled', 'formed', 'ended', 'cancelled'] as LastChanceDisplayStatus[]).map((value) => ({
    label: LAST_CHANCE_DISPLAY_STATUS_LABELS[value],
    value,
  })),
];

const formatDateTime = (value?: string) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '-');
const formatDate = (value?: string) => (value ? moment(value).format('YYYY-MM-DD') : '-');

const LastChanceMonitoringPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [keywordInput, setKeywordInput] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [detailItem, setDetailItem] = useState<LastChanceProductListItem>();
  const [salesStatusOpen, setSalesStatusOpen] = useState(false);
  const [salesStatusItemId, setSalesStatusItemId] = useState<string>();
  const [searchParams] = useSearchParams();
  const initialDisplayStatus = searchParams.get('displayStatus') || '';
  const [searchQuery, setSearchQuery] = useState<LastChanceAdminQuery>({
    page: 0,
    size: 20,
    displayStatus: DISPLAY_STATUS_TABS.some((tab) => tab.value === initialDisplayStatus) ? initialDisplayStatus : '',
    dateType: 'createdAt',
  });

  const listQuery = useQuery({
    queryKey: ['admin-last-chance-products', JSON.stringify(searchQuery)],
    queryFn: () => lastChanceApi.searchLastChanceProducts(searchQuery),
  });

  const summaryQuery = useQuery({
    queryKey: ['admin-last-chance-summary', JSON.stringify(searchQuery)],
    queryFn: () =>
      lastChanceApi.getLastChanceSummary({
        ...searchQuery,
        page: undefined,
        size: undefined,
        displayStatus: undefined,
      }),
  });

  const cancelMutation = useMutation({
    mutationFn: lastChanceApi.cancelLastChanceRegistration,
    onSuccess: (res) => {
      message.success(res.data.message);
      queryClient.invalidateQueries({ queryKey: ['admin-last-chance-products'] });
      queryClient.invalidateQueries({ queryKey: ['admin-last-chance-summary'] });
      setSelectedId(undefined);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      message.error(err.response?.data?.message || '등록 취소에 실패했습니다.');
    },
  });

  const selectedItem = useMemo(
    () => listQuery.data?.data?.contents?.find((item) => item.id === selectedId),
    [listQuery.data?.data?.contents, selectedId],
  );

  const handleCancel = () => {
    if (!selectedItem) {
      message.info('취소할 항목을 선택해 주세요.');
      return;
    }
    if (isLastChanceSaleEnded(selectedItem)) {
      message.error('모집 중인 항목만 등록 취소할 수 있습니다.');
      return;
    }
    if (selectedItem.status !== 'active') {
      message.error('모집 중인 항목만 등록 취소할 수 있습니다.');
      return;
    }
    const hasBuyers = (selectedItem.purchasedCount || 0) > 0;
    Modal.confirm({
      title: '등록 취소',
      okText: '등록 취소',
      cancelText: '닫기',
      content: hasBuyers
        ? '구매자가 있는 상품입니다. 판매 종료 처리되며 기존 구매자 일정은 유지됩니다. 계속할까요?'
        : '라스트찬스 등록을 취소합니다. 계속할까요?',
      onOk: () => cancelMutation.mutateAsync(selectedItem.id),
    });
  };

  const handleExport = async () => {
    try {
      const blob = await lastChanceApi.exportLastChanceProductsExcel({
        ...searchQuery,
        page: undefined,
        size: undefined,
      });
      const fileName = `igogo-last-chance-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch {
      message.error('엑셀 다운로드에 실패했습니다.');
    }
  };

  const columns: ColumnsType<LastChanceProductListItem> = [
    { title: '파트너', dataIndex: 'partnerName', width: 120, ellipsis: true },
    {
      title: '복사',
      width: 110,
      render: (_, record) => {
        const shareUrl = `${PRODUCT_SHARE_BASE_URL}/deals/${record.id}`;
        const productId = record.productId?.toString();
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(record.id);
                toast.dark('상품 ID를 클립보드에 복사했습니다', COPY_TOAST_OPTIONS);
              }}>
              상품ID
            </Button>
            <Button
              disabled={!productId}
              onClick={() => {
                if (!productId) return;
                navigator.clipboard.writeText(productId);
                toast.dark('원상품 ID를 클립보드에 복사했습니다', COPY_TOAST_OPTIONS);
              }}>
              원상품ID
            </Button>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.dark('주소를 클립보드에 복사했습니다', COPY_TOAST_OPTIONS);
              }}>
              공유URL
            </Button>
          </div>
        );
      },
    },
    {
      title: '상품명',
      render: (_, record) => record.productName || record.displayTitle || '-',
      ellipsis: true,
    },
    { title: '옵션', dataIndex: 'optionName', width: 140, ellipsis: true },
    { title: '연령', dataIndex: 'ageBandLabel', width: 100 },
    { title: '수업일', render: (_, r) => formatDate(r.scheduleDate), width: 110 },
    { title: '할인', render: (_, r) => `${r.discountRate ?? 0}%`, width: 70 },
    {
      title: '판매가',
      width: 110,
      render: (_, r) => {
        const hasDiscount = r.baseSalePrice != null && r.baseSalePrice !== r.lastChanceSalePrice;
        return (
          <div style={{ fontSize: 12 }}>
            {hasDiscount && (
              <div style={{ textDecoration: 'line-through', color: '#999' }}>{r.baseSalePrice?.toLocaleString()}원</div>
            )}
            <div style={{ color: hasDiscount ? '#e53e3e' : 'inherit', fontWeight: hasDiscount ? 600 : 400 }}>
              {(hasDiscount ? r.lastChanceSalePrice : r.baseSalePrice ?? r.lastChanceSalePrice)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '모집',
      width: 80,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
          <span>{`${r.purchasedCount ?? 0}/${r.recruitmentCapacity ?? 0}`}</span>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            disabled={(r.purchasedCount ?? 0) <= 0}
            onClick={() => {
              setSalesStatusItemId(r.id);
              setSalesStatusOpen(true);
            }}>
            현황
          </Button>
        </div>
      ),
    },
    { title: '판매마감일', render: (_, r) => formatDateTime(r.deadlineAt), width: 140 },
    {
      title: '상태',
      width: 100,
      render: (_, record) => <LastChanceStatusTag item={record} />,
    },
    {
      title: '등록경로',
      width: 90,
      render: (_, r) => (r.registrationSource === 'agent' ? '에이전트' : '파트너'),
    },
    { title: '등록일', render: (_, r) => formatDateTime(r.createdAt), width: 140 },
    {
      title: '취소 사유',
      width: 160,
      ellipsis: true,
      render: (_, record) =>
        record.cancelReasonDetail ? (
          <Tooltip title={record.cancelReasonDetail}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDetailItem(record)}>
              {record.cancelReasonDetail}
            </Button>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '취소일',
      width: 140,
      render: (_, r) => formatDateTime(r.cancelledAt),
    },
  ];

  const summary = summaryQuery.data?.data;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <ReservationSuccessPageHeader
          title="라스트찬스 모니터링"
          description="위험 회차의 남은 자리를 특가로 다시 연 상품과 구매 현황입니다. 상태 카드를 누르면 해당 상태만 봅니다."
          extra={
            <Space>
              <Button onClick={handleExport}>엑셀 다운로드</Button>
              <Button danger disabled={!selectedId} loading={cancelMutation.isPending} onClick={handleCancel}>
                등록 취소
              </Button>
            </Space>
          }
        />

        <Row gutter={16}>
          <Col span={4}>
            <Card
              hoverable
              onClick={() => setSearchQuery((q) => ({ ...q, displayStatus: '', page: 0 }))}
              style={!searchQuery.displayStatus ? { borderColor: '#1890ff' } : undefined}>
              <Statistic
                title="전체"
                value={
                  summary?.byDisplayStatus
                    ? Object.values(summary.byDisplayStatus).reduce((sum, count) => sum + count, 0)
                    : 0
                }
                loading={summaryQuery.isLoading}
              />
            </Card>
          </Col>
          {(['active', 'fulfilled', 'formed', 'ended', 'cancelled'] as LastChanceDisplayStatus[]).map((status) => (
            <Col span={4} key={status}>
              <Card
                hoverable
                onClick={() => setSearchQuery((q) => ({ ...q, displayStatus: status, page: 0 }))}
                style={searchQuery.displayStatus === status ? { borderColor: '#1890ff' } : undefined}>
                <Statistic
                  title={LAST_CHANCE_DISPLAY_STATUS_LABELS[status]}
                  value={summary?.byDisplayStatus?.[status] ?? 0}
                  loading={summaryQuery.isLoading}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Segmented
              options={DISPLAY_STATUS_TABS}
              value={searchQuery.displayStatus || ''}
              onChange={(value) => setSearchQuery((q) => ({ ...q, displayStatus: value as string, page: 0 }))}
            />
            <FilterSelect
              inactiveValue="createdAt"
              style={{ width: 120 }}
              value={searchQuery.dateType || 'createdAt'}
              onChange={(value) => setSearchQuery((q) => ({ ...q, dateType: value, page: 0 }))}
              options={[
                { value: 'createdAt', label: '등록일' },
                { value: 'scheduleDate', label: '수업일' },
                { value: 'deadlineAt', label: '판매마감일' },
              ]}
            />
            <DatePicker.RangePicker
              value={
                searchQuery.startDate && searchQuery.endDate
                  ? [dayjs(searchQuery.startDate), dayjs(searchQuery.endDate)]
                  : undefined
              }
              onChange={(dates) =>
                setSearchQuery((q) => ({
                  ...q,
                  page: 0,
                  startDate: dates?.[0]?.toISOString(),
                  endDate: dates?.[1]?.toISOString(),
                }))
              }
            />
            <FilterSelect
              inactiveValue=""
              style={{ width: 120 }}
              value={searchQuery.createdByAgent || ''}
              onChange={(value) =>
                setSearchQuery((q) => ({
                  ...q,
                  createdByAgent: value as LastChanceAdminQuery['createdByAgent'],
                  page: 0,
                }))
              }
              options={[
                { value: '', label: '등록경로 전체' },
                { value: 'false', label: '파트너' },
                { value: 'true', label: '에이전트' },
              ]}
            />
            <Input.Search
              className={filterActiveClassName(!!searchQuery.keyword)}
              placeholder="상품명, 옵션, 파트너 검색"
              style={{ width: 240 }}
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onSearch={(value) => setSearchQuery((q) => ({ ...q, keyword: value, page: 0 }))}
              allowClear
            />
            <Button
              onClick={() => {
                setKeywordInput('');
                setSearchQuery({ page: 0, size: 20, displayStatus: '', dateType: 'createdAt' });
              }}>
              초기화
            </Button>
          </Space>

          <div style={{ marginBottom: 8 }}>검색결과: {listQuery.data?.data?.total ?? 0}</div>

          <Table
            rowKey="id"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={listQuery.data?.data?.contents || []}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedId ? [selectedId] : [],
              onChange: (keys) => setSelectedId(keys[0] as string),
            }}
            pagination={{
              current: (searchQuery.page || 0) + 1,
              pageSize: searchQuery.size || 20,
              total: listQuery.data?.data?.total || 0,
              showSizeChanger: true,
              onChange: (page, size) => setSearchQuery((q) => ({ ...q, page: page - 1, size })),
            }}
            scroll={{ x: 1400 }}
          />
        </Card>
      </Space>

      <Modal
        title="등록 취소 상세"
        open={!!detailItem}
        onCancel={() => setDetailItem(undefined)}
        footer={[
          <Button key="close" onClick={() => setDetailItem(undefined)}>
            닫기
          </Button>,
        ]}>
        {detailItem && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <strong>상품</strong>
              <div>{detailItem.productName || detailItem.displayTitle || '-'}</div>
            </div>
            <div>
              <strong>상태</strong>
              <div>
                <LastChanceStatusTag item={detailItem} />
              </div>
            </div>
            <div>
              <strong>취소 사유</strong>
              <div style={{ whiteSpace: 'pre-wrap' }}>{detailItem.cancelReasonDetail || '-'}</div>
            </div>
            <div>
              <strong>취소일</strong>
              <div>{formatDateTime(detailItem.cancelledAt)}</div>
            </div>
            <div>
              <strong>취소 주체</strong>
              <div>
                {detailItem.cancelledBy === 'partner' ? '파트너' : detailItem.cancelledBy === 'admin' ? '관리자' : '-'}
              </div>
            </div>
            {(detailItem.refundedBuyerCount || 0) > 0 && (
              <>
                <div>
                  <strong>환불 인원</strong>
                  <div>{detailItem.refundedBuyerCount}명</div>
                </div>
                <div>
                  <strong>환불 금액</strong>
                  <div>{(detailItem.refundedAmount ?? 0).toLocaleString()}원</div>
                </div>
              </>
            )}
          </Space>
        )}
      </Modal>

      <LastChanceSalesStatusModal
        open={salesStatusOpen}
        itemId={salesStatusItemId}
        onClose={() => {
          setSalesStatusOpen(false);
          setSalesStatusItemId(undefined);
        }}
      />
    </div>
  );
};

export default LastChanceMonitoringPage;
